import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  setPage,
  setSelectedAccount,
  setSelectedBrands,
  setSelectedCompany,
  setSelectedInterface,
  setSelectedRawTableId,
  setSelectedStatus,
  setSelectedWebsites,
} from "../../../../features/dataCollectionBrandsSlice";
import { Box } from "@mui/system";
import FilterComponent from "../../../ReusableFIlterComponent/FilterComponent";

const BrandsFilter = ({ clearSelectedDataCollectionBrands }: any) => {
  const {
    selectedAccount,
    selectedCompany,
    selectedWebsites,
    selectedStatus,
    selectedRawTableId,
    dataCollectionBrandsList,
    selectedBrands,
    selectedInterface,
  } = useSelector((state: any) => state.dataCollectionBrands);
  const dispatch = useDispatch();
  const [searchBrands, setSearchBrands] = useState("");
  const [searchInterface, setSearchInterface] = useState("");
  const [searchAccount, setSearchAccount] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [searchWebsites, setSearchWebsites] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchRawTableId, setSearchRawTableId] = useState("");

  const resetPagination = () => {
    dispatch(setPage(0));
  };

  const brandMap = useMemo(
    () =>
      new Map(
        dataCollectionBrandsList?.map((item: any) => [
          item.brand_id,
          item.brand_name,
        ]) || []
      ),
    [dataCollectionBrandsList]
  );
  const interfaceMap = useMemo(
    () =>
      new Map(
        dataCollectionBrandsList?.map((item: any) => [
          item.interface_id,
          item.interface_name,
        ]) || []
      ),
    [dataCollectionBrandsList]
  );

  const matchedBrands = Array.from(
    new Set(
      dataCollectionBrandsList
        ?.map((pkg: any) => pkg.brand_id)
        .filter((id: any) => id !== null && id !== undefined)
    )
  ) // Get unique brand IDs
    .filter((brandId) => brandMap.has(brandId)) // Only keep those present in brandMap
    .map((brandId) => ({
      brand_name: `${brandMap.get(brandId)} (${brandId})`, // combination of brandName and id. example : pixiebet.com Sport UK(1153)
    }));

  const matchedInterface = Array.from(
    new Set(
      dataCollectionBrandsList
        ?.map((pkg: any) => pkg.interface_id)
        .filter((id: any) => id !== null && id !== undefined) //(we filter out null and undefined values to avoid errors)
    )
  ) // Get unique brand IDs
    .filter((interfaceId) => interfaceMap.has(interfaceId)) // Only keep those present in brandMap
    .map((interfaceId) => ({
      interface_name: `${interfaceMap.get(interfaceId)} (${interfaceId})`, // combination of brandName and id. example : pixiebet.com Sport UK(1153)
    }));

  const filterConfig = [
    {
      key: "Brands",
      label: "Select one Brands or more",
      searchTerm: searchBrands,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchBrands(e.target.value),
      selectedOption: selectedBrands,
      filterName: "brand_name",
      data: matchedBrands,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedBrands(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedBrands && selectedBrands.length,
      colorSelected:
        selectedBrands !== null && selectedBrands.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Account",
      label: "Select one Account or more",
      searchTerm: searchAccount,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchAccount(e.target.value),
      selectedOption: selectedAccount,
      filterName: "username",
      data: dataCollectionBrandsList,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedAccount(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedAccount && selectedAccount.length,
      colorSelected:
        selectedAccount !== null && selectedAccount.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Company",
      label: "Select one Company or more",
      searchTerm: searchCompany,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchCompany(e.target.value),
      selectedOption: selectedCompany,
      filterName: "company_name",
      data: dataCollectionBrandsList,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedCompany(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedCompany && selectedCompany.length,
      colorSelected:
        selectedCompany !== null && selectedCompany.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Websites",
      label: "Select one Website or more",
      searchTerm: searchWebsites,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchWebsites(e.target.value),
      selectedOption: selectedWebsites,
      filterName: "site_name",
      data: dataCollectionBrandsList,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedWebsites(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedWebsites && selectedWebsites.length,
      colorSelected:
        selectedWebsites !== null && selectedWebsites.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Status",
      label: "Select a Status",
      searchTerm: searchStatus,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchStatus(e.target.value),
      selectedOption: selectedStatus,
      filterName: "brand_isactive",
      data: dataCollectionBrandsList,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedStatus(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedStatus && selectedStatus.length,
      colorSelected:
        selectedStatus !== null && selectedStatus.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: false,
      showSearch: false,
      valueMapping: { 1: "Active", 0: "Inactive" },
    },
    {
      key: "Raw Table ID",
      label: "Select one Raw Table ID or more",
      searchTerm: searchRawTableId,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchRawTableId(e.target.value),
      selectedOption: selectedRawTableId,
      filterName: "raw_output_table_name",
      data: dataCollectionBrandsList.map((item: any) => ({
        raw_output_table_name: item?.raw_output_table_name?.map(
          (table: any) => table.table_id
        ),
      })),
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedRawTableId(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedRawTableId && selectedRawTableId.length,
      colorSelected:
        selectedRawTableId !== null && selectedRawTableId.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
    {
      key: "Interface",
      label: "Select one Interface or more",
      searchTerm: searchInterface,
      onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setSearchInterface(e.target.value),
      selectedOption: selectedInterface,
      filterName: "interface_name",
      data: matchedInterface,
      onSelectionChange: (updatedSelection: any) =>
        dispatch(setSelectedInterface(updatedSelection)),
      onSelectionApplied: resetPagination,
      count: selectedInterface && selectedInterface.length,
      colorSelected:
        selectedInterface !== null && selectedInterface.length > 0
          ? "#9C27B0"
          : "#000000DE",
      multiSelect: true,
      showSearch: true,
    },
  ];

  const clearFilters = () => {
    dispatch(setSelectedAccount(null));
    dispatch(setSelectedCompany(null));
    dispatch(setSelectedWebsites(null));
    dispatch(setSelectedStatus(null));
    dispatch(setSelectedRawTableId(null));
    dispatch(setSelectedInterface(null));
    dispatch(setSelectedBrands(null));
    dispatch(setPage(0));
    clearSelectedDataCollectionBrands(null);
    setSearchAccount("");
    setSearchCompany("");
    setSearchWebsites("");
    setSearchStatus("");
    setSearchRawTableId("");
    setSearchInterface("");
    setSearchBrands("");
  };
  return (
    <Box data-tracker="358">
      <FilterComponent
        filterConfig={filterConfig}
        clearFilters={clearFilters}
      />
    </Box>
  );
};

export default BrandsFilter;
